import React, { useState } from "react";

import { setPrefCurrency } from "../services/settingsService";
import { Loader2 } from "lucide-react";
import ReactCountryFlag from "react-country-flag";
import CustomDropdown from "./ui/custom-dropdown";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

// Currently only ZAR is supported. This array can be expanded in the future
// to include additional currencies when they become available.
const SUPPORTED_CURRENCIES = [
  {
    code: "ZAR",
    name: "South African Rand",
    symbol: "R",
    countries: [{ name: "South Africa", countryCode: "ZA" }],
  },
];

function CurrencyDropdownTransparentBlack({ localCurrency }) {
  const [isLoading, setIsLoading] = useState(false);
  const { userProfile, updateSettings } = useAuth();
  const { toast } = useToast();
  const [selectedCurrency, setSelectedCurrency] = useState(
    localCurrency || "ZAR"
  );

  const setCurrencyApi = async (data) => {
    try {
      setIsLoading(true);

      const authId = userProfile?.authId;
      const apiCall = await setPrefCurrency(data, authId);
      if (apiCall.status === 200 && apiCall.data.status === true) {
        const newSettings = apiCall.data.data;
        updateSettings(newSettings);
        setIsLoading(false);
        toast({
          description: "Currency Updated Successfully",
          variant: "eighty",
        });
      } else {
        setIsLoading(false);
        console.error("Currency update failed:", apiCall);
        toast({
          description: apiCall?.data?.message || "Error Updating Currency",
          variant: "destructive",
        });
      }
    } catch (err) {
      setIsLoading(false);
      console.error("Currency update error:", err);
      toast({
        description: err?.response?.data?.message || "Error Updating Currency",
        variant: "destructive",
      });
    }
  };

  function currencyChange(cur) {
    const currencyCode = cur.code;
    setSelectedCurrency(currencyCode);
    setCurrencyApi(currencyCode);
  }

  // Filter function for currencies
  const filterCurrencies = (currencies, searchTerm) => {
    if (!searchTerm) return currencies;
    return currencies.filter(
      (currency) =>
        currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Render currency item in dropdown
  const renderCurrencyItem = (currency, isSelected) => (
    <div
      className={`flex items-center justify-between px-4 py-3 cursor-pointer border rounded-md mb-2 ${
        isSelected ? "border-rose-300 bg-rose-50" : "border-gray-200"
      } hover:bg-gray-100`}
    >
      <div className="flex items-center">
        <ReactCountryFlag
          countryCode={currency.countries[0]?.countryCode || ""}
          svg
          className="w-6 h-6 mr-2"
          title={currency.countries[0]?.name || ""}
        />
        <span className="text-sm font-medium text-gray-800">
          {currency.code}
        </span>
      </div>
      <span className="text-sm text-gray-500">{currency.name}</span>
    </div>
  );

  // Render trigger button
  const renderTrigger = () => {
    if (isLoading) {
      return <Loader2 className="animate-spin text-gray-700" />;
    }
    return (
      <>
        <span className="text-sm font-semibold">{selectedCurrency}</span>
        <span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </>
    );
  };

  // Find the full currency object for the selected code
  const selectedCurrencyObj =
    SUPPORTED_CURRENCIES.find((cur) => cur.code === selectedCurrency) ||
    SUPPORTED_CURRENCIES[0];

  return (
    <CustomDropdown
      items={SUPPORTED_CURRENCIES}
      selectedItem={selectedCurrencyObj}
      onSelect={currencyChange}
      renderItem={renderCurrencyItem}
      renderTrigger={renderTrigger}
      filterItems={filterCurrencies}
      placeholder="Search currencies..."
      title="Select a local currency"
      description="Choose your default currency for transactions and pricing across the 80eight platform."
      fullWidth={false}
    />
  );
}

export default CurrencyDropdownTransparentBlack;
